import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, User, Mail, Phone, Loader2, CheckCircle, 
  AlertTriangle, ExternalLink, Zap, Shield, Check, AtSign
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Risk disclaimers for Inside Bar Algo (same 10-point checklist)
const DISCLAIMERS = [
  "I understand that trading involves substantial risk of loss.",
  "Past performance does not guarantee future results.",
  "I am trading with capital I can afford to lose.",
  "I understand the algorithm may have losing periods.",
  "I have read and understood the risk disclosure.",
  "I will not hold Monk Algo liable for any trading losses.",
  "I understand this is a tool, not financial advice.",
  "I am of legal age to trade in my jurisdiction.",
  "I understand subscription fees are non-refundable.",
  "I agree to the Terms of Service and Privacy Policy."
];

export const InsideBarSubscribeModal = ({ isOpen, onClose }) => {
  const [step, setStep] = useState(1); // 1: Form, 2: Disclaimers, 3: Payment
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    tradingview_username: ''
  });
  const [acceptedDisclaimers, setAcceptedDisclaimers] = useState(
    new Array(DISCLAIMERS.length).fill(false)
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [paymentLink, setPaymentLink] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const toggleDisclaimer = (index) => {
    setAcceptedDisclaimers(prev => {
      const newState = [...prev];
      newState[index] = !newState[index];
      return newState;
    });
  };

  const selectAllDisclaimers = () => {
    const allSelected = acceptedDisclaimers.every(d => d);
    setAcceptedDisclaimers(new Array(DISCLAIMERS.length).fill(!allSelected));
  };

  const allDisclaimersAccepted = acceptedDisclaimers.every(d => d);

  const validateForm = () => {
    if (!formData.first_name.trim()) return 'First name is required';
    if (!formData.last_name.trim()) return 'Last name is required';
    if (!formData.email.trim()) return 'Email is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) return 'Invalid email format';
    if (!formData.phone.trim()) return 'Phone number is required';
    if (!formData.tradingview_username.trim()) return 'TradingView username is required';
    return null;
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }
    setStep(2);
  };

  const handleDisclaimerSubmit = async () => {
    if (!allDisclaimersAccepted) {
      setError('Please accept all disclaimers to continue');
      return;
    }

    setLoading(true);
    setError('');

    // Hardcoded Razorpay payment link
    const RAZORPAY_LINK = "https://rzp.io/rzp/FnAh1YT";

    try {
      const response = await fetch(`${API_URL}/api/inside-bar/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          disclaimers_accepted: true,
          disclaimers_accepted_at: new Date().toISOString(),
          accepted_disclaimers: DISCLAIMERS
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Use API payment link if available, otherwise use hardcoded link
        setPaymentLink(data.payment_link || RAZORPAY_LINK);
        setStep(3);
      } else {
        setError(data.detail || 'Failed to process subscription');
      }
    } catch (err) {
      // Even if API fails, allow proceeding with hardcoded payment link
      setPaymentLink(RAZORPAY_LINK);
      setStep(3);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setFormData({ first_name: '', last_name: '', email: '', phone: '', tradingview_username: '' });
    setAcceptedDisclaimers(new Array(DISCLAIMERS.length).fill(false));
    setError('');
    setPaymentLink('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={handleClose}
      />
      
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="relative w-full max-w-lg bg-[#0a0a0f] rounded-2xl border border-white/10 shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
      >
        {/* Header */}
        <div className="p-6 border-b border-white/10 bg-gradient-to-r from-[#8B5CF6]/10 to-[#10B981]/10 flex-shrink-0">
          <button 
            onClick={handleClose}
            className="absolute top-4 right-4 w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
          >
            <X size={16} className="text-white/60" />
          </button>
          
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#8B5CF6]/20 to-[#10B981]/20 flex items-center justify-center">
              <Zap className="w-6 h-6 text-[#8B5CF6]" />
            </div>
            <div>
              <h2 className="font-heading font-bold text-xl text-white">Inside Bar TradingView Algo</h2>
              <p className="font-body text-sm text-white/50">
                {step === 1 && 'Enter your details'}
                {step === 2 && 'Review risk disclaimers'}
                {step === 3 && 'Complete payment'}
              </p>
            </div>
          </div>

          {/* Step Indicator */}
          <div className="flex items-center gap-2 mt-4">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center gap-2">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${
                  s < step ? 'bg-[#10B981] text-white' :
                  s === step ? 'bg-[#8B5CF6] text-white' :
                  'bg-white/10 text-white/40'
                }`}>
                  {s < step ? <Check size={12} /> : s}
                </div>
                {s < 3 && <div className={`w-8 h-0.5 ${s < step ? 'bg-[#10B981]' : 'bg-white/10'}`} />}
              </div>
            ))}
          </div>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto">
          {/* Step 1: Form */}
          {step === 1 && (
            <form onSubmit={handleFormSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-body text-xs text-white/50 mb-1.5 block">First Name *</label>
                  <div className="relative">
                    <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
                    <input
                      type="text"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleInputChange}
                      className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-[#8B5CF6]/50"
                      placeholder="John"
                      data-testid="ib-subscribe-first-name"
                    />
                  </div>
                </div>
                <div>
                  <label className="font-body text-xs text-white/50 mb-1.5 block">Last Name *</label>
                  <input
                    type="text"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-[#8B5CF6]/50"
                    placeholder="Doe"
                    data-testid="ib-subscribe-last-name"
                  />
                </div>
              </div>

              <div>
                <label className="font-body text-xs text-white/50 mb-1.5 block">Email *</label>
                <div className="relative">
                  <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-[#8B5CF6]/50"
                    placeholder="john@example.com"
                    data-testid="ib-subscribe-email"
                  />
                </div>
              </div>

              <div>
                <label className="font-body text-xs text-white/50 mb-1.5 block">Phone *</label>
                <div className="relative">
                  <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-[#8B5CF6]/50"
                    placeholder="+91 98765 43210"
                    data-testid="ib-subscribe-phone"
                  />
                </div>
              </div>

              <div>
                <label className="font-body text-xs text-white/50 mb-1.5 block">TradingView Username *</label>
                <div className="relative">
                  <AtSign size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
                  <input
                    type="text"
                    name="tradingview_username"
                    value={formData.tradingview_username}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-[#8B5CF6]/50"
                    placeholder="Enter your TradingView username"
                    data-testid="ib-subscribe-tradingview-username"
                  />
                </div>
                <p className="text-xs text-white/30 mt-1">Your TradingView account will receive strategy access</p>
              </div>

              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <AlertTriangle size={16} className="text-red-400" />
                  <span className="text-sm text-red-400">{error}</span>
                </div>
              )}

              <button
                type="submit"
                className="w-full py-3.5 bg-gradient-to-r from-[#8B5CF6] to-[#10B981] rounded-xl text-white font-heading font-semibold text-sm transition-all hover:opacity-90"
                data-testid="ib-subscribe-continue-btn"
              >
                Continue to Risk Disclosure
              </button>
            </form>
          )}

          {/* Step 2: Disclaimers */}
          {step === 2 && (
            <div className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-[#F59E0B]" />
                  <span className="font-heading font-semibold text-white text-sm">Risk Acknowledgment</span>
                </div>
                <button
                  onClick={selectAllDisclaimers}
                  className="text-xs text-[#8B5CF6] hover:text-[#9B6CF6] transition-colors"
                >
                  {allDisclaimersAccepted ? 'Deselect All' : 'Select All'}
                </button>
              </div>

              <div className="space-y-2 max-h-[300px] overflow-y-auto pr-2">
                {DISCLAIMERS.map((disclaimer, index) => (
                  <label
                    key={index}
                    className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                      acceptedDisclaimers[index] 
                        ? 'bg-[#10B981]/10 border border-[#10B981]/30' 
                        : 'bg-white/3 border border-white/5 hover:border-white/10'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={acceptedDisclaimers[index]}
                      onChange={() => toggleDisclaimer(index)}
                      className="mt-0.5 w-4 h-4 rounded border-white/30 bg-white/5 text-[#10B981] focus:ring-[#10B981] focus:ring-offset-0"
                    />
                    <span className="font-body text-xs text-white/70 leading-relaxed">{disclaimer}</span>
                  </label>
                ))}
              </div>

              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <AlertTriangle size={16} className="text-red-400" />
                  <span className="text-sm text-red-400">{error}</span>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={() => setStep(1)}
                  className="flex-1 py-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-white font-medium text-sm transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={handleDisclaimerSubmit}
                  disabled={!allDisclaimersAccepted || loading}
                  className="flex-1 py-3 bg-gradient-to-r from-[#8B5CF6] to-[#10B981] rounded-xl text-white font-heading font-semibold text-sm flex items-center justify-center gap-2 transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="ib-subscribe-accept-btn"
                >
                  {loading ? <Loader2 size={18} className="animate-spin" /> : <Check size={18} />}
                  {loading ? 'Processing...' : 'I Accept & Continue'}
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Payment */}
          {step === 3 && (
            <div className="p-6 text-center">
              <div className="w-16 h-16 rounded-full bg-[#10B981]/10 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-[#10B981]" />
              </div>
              <h3 className="font-heading font-bold text-lg text-white mb-2">Almost There!</h3>
              <p className="font-body text-sm text-white/50 mb-6">
                Click below to complete your subscription payment via Razorpay.
              </p>

              {paymentLink ? (
                <a
                  href={paymentLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full py-3.5 bg-gradient-to-r from-[#8B5CF6] to-[#10B981] rounded-xl text-white font-heading font-semibold text-sm flex items-center justify-center gap-2 transition-all hover:opacity-90"
                  data-testid="ib-subscribe-payment-btn"
                >
                  <Zap size={18} />
                  Proceed to Payment
                  <ExternalLink size={14} className="opacity-70" />
                </a>
              ) : (
                <div className="w-full py-3.5 bg-white/5 border border-white/10 rounded-xl text-white/40 font-heading text-sm flex items-center justify-center gap-2">
                  <AlertTriangle size={18} />
                  Payment link not configured yet
                </div>
              )}

              <button
                onClick={handleClose}
                className="w-full mt-3 py-2.5 text-white/50 hover:text-white text-sm transition-colors"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};
